// 管理端统一 API 请求层：相对路径 /api/v1（nginx 反代到 wmadmin_backend 6110）
// 统一响应 { code: 0, data, message }；401 自动 refresh 一次；失败抛 ApiError
const BASE = '/api/v1';

const TOKEN_KEY = 'mn_admin_token';
const REFRESH_KEY = 'mn_admin_refresh';

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}
export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_KEY);
}
export function setTokens(access: string, refresh?: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}
export function clearTokens(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}
export function isLoggedIn(): boolean {
  return !!getToken();
}

export class ApiError extends Error {
  code: number;
  constructor(code: number, message: string) {
    super(message);
    this.code = code;
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | undefined>;
  auth?: boolean;
}

async function tryRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    const json = await res.json();
    if (json?.code === 0 && json?.data?.accessToken) {
      setTokens(json.data.accessToken, json.data.refreshToken || rt);
      return true;
    }
  } catch {
    /* 忽略 */
  }
  return false;
}

export async function request<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = 'GET', body, params, auth = true } = opts;
  let url = BASE + path;
  if (params) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    }
    const s = qs.toString();
    if (s) url += '?' + s;
  }
  const headers: Record<string, string> = {};
  const token = getToken();
  if (auth && token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(url, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  let json: { code?: number; data?: unknown; message?: string } | null = null;
  try {
    json = await res.json();
  } catch {
    /* 非 JSON */
  }

  if (res.status === 401 && auth && token) {
    const ok = await tryRefresh();
    if (ok) return request<T>(path, opts);
    clearTokens();
    if (typeof window !== 'undefined') window.location.href = '/login';
    throw new ApiError(401, json?.message || 'Unauthorized');
  }

  if (json && json.code === 0) return json.data as T;
  throw new ApiError(json?.code ?? res.status, json?.message || `HTTP ${res.status}`);
}

export interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

// ===== captchaApi（图形验证码）=====
export const captchaApi = {
  get: () => request<{ captchaId: string; image: string }>('/auth/captcha', { auth: false }),
};

// ===== authApi =====
export const authApi = {
  login: (body: { email: string; password: string; captchaId: string; captchaCode: string }) =>
    request<{ step: string; method?: string; devCode?: string; twoFaEnabled?: boolean } & Partial<TokenData>>('/auth/login', { method: 'POST', body, auth: false }),
  loginVerify: (body: { email: string; method: 'email' | 'google'; code: string }) =>
    request<{ step: string } & TokenData>('/auth/login/verify', { method: 'POST', body, auth: false }),
  me: () => request<{ id: string; email: string; name?: string | null; role?: { code: string; name: string } | null; permissions?: string[] }>('/auth/me'),
  logout: (refreshToken: string) => request<{ loggedOut: boolean }>('/auth/logout', { method: 'POST', body: { refreshToken } }),
  twoFaStatus: () => request<{ enabled: boolean; method: string | null }>('/auth/2fa/status'),
  twoFaSetup: () => request<{ secret: string; otpauthUrl: string }>('/auth/2fa/setup', { method: 'POST' }),
  twoFaConfirm: (body: { secret: string; code: string }) => request<{ enabled: boolean }>('/auth/2fa/confirm', { method: 'POST', body }),
  twoFaDisable: (body: { code: string }) => request<{ disabled: boolean }>('/auth/2fa/disable', { method: 'POST', body }),
};

// ===== dashboardApi =====
export const dashboardApi = {
  stats: () => request<Record<string, unknown>>('/dashboard/stats'),
  todos: () => request<unknown[]>('/dashboard/todos'),
  alerts: () => request<unknown[]>('/dashboard/alerts'),
  recentOps: () => request<unknown[]>('/dashboard/recent-operations'),
};

// ===== assetsApi =====
export const assetsApi = {
  fields: () => request<unknown[]>('/assets/fields'),
  fieldCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/assets/fields', { method: 'POST', body }),
  fieldUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/assets/fields/${id}`, { method: 'PUT', body }),
  wells: (params?: { status?: string }) => request<unknown[]>('/assets/wells', { params }),
  wellCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/assets/wells', { method: 'POST', body }),
  wellUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/assets/wells/${id}`, { method: 'PUT', body }),
  wellsImport: (rows: Record<string, unknown>[]) => request<{ imported: number }>('/assets/wells/import', { method: 'POST', body: { rows } }),
  gisTemplates: () => request<{ templateUrl: string }>('/assets/gis/templates'),
  gisImport: (body: { fieldId: string; rows: Record<string, unknown>[] }) => request<{ imported: number }>('/assets/gis/import', { method: 'POST', body }),
  // RWA 资产页（产品发布表单 ↔ wells 资产字段 ↔ 投资人端展示）
  wellAssetPage: (id: string) => request<{ well: Record<string, unknown>; documents: WellDocument[] }>(`/assets/wells/${id}/asset-page`),
  wellPublish: (id: string, body: Record<string, unknown>) => request<Record<string, unknown>>(`/assets/wells/${id}/publish`, { method: 'PUT', body }),
  wellUnpublish: (id: string) => request<{ unpublished: boolean }>(`/assets/wells/${id}/unpublish`, { method: 'POST' }),
  documents: (id: string) => request<WellDocument[]>(`/assets/wells/${id}/documents`),
  documentCreate: (id: string, body: Record<string, unknown>) => request<WellDocument>(`/assets/wells/${id}/documents`, { method: 'POST', body }),
  documentDelete: (id: string, docId: string) => request<{ deleted: boolean }>(`/assets/wells/${id}/documents/${docId}`, { method: 'DELETE' }),
  upload: async (file: File): Promise<{ objectUrl: string; filename: string; contentType: string; size: number }> => {
    const form = new FormData();
    form.append('file', file);
    const token = getToken();
    const res = await fetch(`${BASE}/assets/uploads`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : undefined, body: form });
    const json = await res.json().catch(() => null);
    if (json && json.code === 0) return json.data;
    throw new ApiError(json?.code ?? res.status, json?.message || `HTTP ${res.status}`);
  },
};

export interface WellDocument {
  id: string;
  wellId: string;
  type: string; // report/production/photo/video/reserve/contract/settlement
  title: string;
  url: string;
  period?: string | null;
  size?: number | null;
  mime?: string | null;
  createdAt?: string;
}

// ===== dataApi =====
export const dataApi = {
  sources: () => request<unknown[]>('/data/sources'),
  sourceCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/data/sources', { method: 'POST', body }),
  sourceUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/data/sources/${id}`, { method: 'PUT', body }),
  tasks: () => request<unknown[]>('/data/tasks'),
  taskRetry: (id: string) => request<{ retried: boolean }>(`/data/tasks/${id}/retry`, { method: 'POST' }),
  entryCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/data/entries', { method: 'POST', body }),
  quality: () => request<unknown[]>('/data/quality'),
  qualityHandle: (id: string, body: Record<string, unknown>) => request<{ handled: boolean }>(`/data/quality/${id}/handle`, { method: 'POST', body }),
};

// ===== projectsApi =====
export const projectsApi = {
  list: () => request<unknown[]>('/projects'),
  create: (body: Record<string, unknown>) => request<Record<string, unknown>>('/projects', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/projects/${id}`, { method: 'PUT', body }),
  status: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/projects/${id}/status`, { method: 'POST', body }),
  detail: (id: string) => request<Record<string, unknown>>(`/projects/${id}`),
};

// ===== ordersApi =====
export const ordersApi = {
  list: (params?: { type?: string; status?: string; page?: number; page_size?: number }) =>
    request<{ list: unknown[]; total: number; page: number; page_size: number }>('/orders', { params }),
  detail: (id: string) => request<Record<string, unknown>>(`/orders/${id}`),
  approve: (id: string) => request<{ approved: boolean }>(`/orders/${id}/approve`, { method: 'POST' }),
  reject: (id: string, body: { reason: string }) => request<{ rejected: boolean }>(`/orders/${id}/reject`, { method: 'POST', body }),
};

// ===== equitiesApi =====
export const equitiesApi = {
  list: () => request<unknown[]>('/equities'),
  review: (id: string, body: { decision: 'approve' | 'reject'; reason?: string }) =>
    request<{ reviewed: boolean }>(`/equities/${id}/review`, { method: 'POST', body }),
  settle: (id: string) => request<{ settled: boolean }>(`/equities/${id}/settle`, { method: 'POST' }),
  statistics: () => request<Record<string, unknown>>('/equities/statistics'),
};

// ===== fundsApi =====
export const fundsApi = {
  deposits: () => request<unknown[]>('/funds/deposits'),
  depositApprove: (id: string) => request<{ approved: boolean }>(`/funds/deposits/${id}/approve`, { method: 'POST' }),
  depositReject: (id: string, body: { reason: string }) => request<{ rejected: boolean }>(`/funds/deposits/${id}/reject`, { method: 'POST', body }),
  withdrawals: () => request<unknown[]>('/funds/withdrawals'),
  withdrawalApprove: (id: string) => request<{ approved: boolean }>(`/funds/withdrawals/${id}/approve`, { method: 'POST' }),
  withdrawalReject: (id: string, body: { reason: string }) => request<{ rejected: boolean }>(`/funds/withdrawals/${id}/reject`, { method: 'POST', body }),
  reconciliation: () => request<unknown[]>('/funds/reconciliation'),
  reconciliationResolve: (id: string) => request<{ resolved: boolean }>(`/funds/reconciliation/${id}/resolve`, { method: 'POST' }),
  plans: () => request<unknown[]>('/funds/plans'),
  planToggle: (id: string) => request<Record<string, unknown>>(`/funds/plans/${id}/toggle`, { method: 'POST' }),
  usdtWallets: () => request<unknown[]>('/funds/usdt-wallets'),
};

// ===== kycApi =====
export const kycApi = {
  applications: (params?: { status?: string }) => request<{ list: unknown[]; total: number }>('/kyc/applications', { params }),
  detail: (id: string) => request<Record<string, unknown>>(`/kyc/applications/${id}`),
  approve: (id: string) => request<{ approved: boolean }>(`/kyc/applications/${id}/approve`, { method: 'POST' }),
  reject: (id: string, body: { reason: string }) => request<{ rejected: boolean }>(`/kyc/applications/${id}/reject`, { method: 'POST', body }),
  notice: (id: string) => request<{ noticed: boolean }>(`/kyc/applications/${id}/notice`, { method: 'POST' }),
};

// ===== investorsApi =====
export const investorsApi = {
  list: (params?: { status?: string; page?: number; page_size?: number }) =>
    request<{ list: unknown[]; total: number; page: number; page_size: number }>('/investors', { params }),
  detail: (id: string) => request<Record<string, unknown>>(`/investors/${id}`),
  freeze: (id: string) => request<{ frozen: boolean }>(`/investors/${id}/freeze`, { method: 'POST' }),
  unfreeze: (id: string) => request<{ unfrozen: boolean }>(`/investors/${id}/unfreeze`, { method: 'POST' }),
  blacklist: () => request<unknown[]>('/investors/blacklist'),
  blacklistCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/investors/blacklist', { method: 'POST', body }),
  blacklistUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/investors/blacklist/${id}`, { method: 'PUT', body }),
  blacklistSync: () => request<{ synced: number }>('/investors/blacklist/sync', { method: 'POST' }),
};

// ===== revenueApi =====
export const revenueApi = {
  records: () => request<unknown[]>('/revenue/records'),
  update: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/revenue/records/${id}`, { method: 'PUT', body }),
  generate: (recordId: string) => request<{ generated: number }>('/revenue/generate', { method: 'POST', body: { recordId } }),
  losses: () => request<unknown[]>('/revenue/losses'),
  exportData: () => request<{ exported: boolean }>('/revenue/export', { method: 'POST' }),
};

// ===== financeApi =====
export const financeApi = {
  periods: () => request<unknown[]>('/finance/periods'),
  periodLock: (id: string) => request<{ locked: boolean }>(`/finance/periods/${id}/lock`, { method: 'POST' }),
  reports: () => request<unknown[]>('/finance/reports'),
  reportGenerate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/finance/reports', { method: 'POST', body }),
  reportDownload: (id: string) => request<{ downloadUrl: string }>(`/finance/reports/${id}/download`),
  holdingsSummary: () => request<unknown[]>('/finance/holdings-summary'),
  trend: () => request<Array<{ period: string; revenue: number; cost: number; profit: number }>>('/finance/trend'),
};

// ===== channelApi =====
export const channelApi = {
  referrers: () => request<unknown[]>('/channel/referrers'),
  network: (id: string) => request<Record<string, unknown>>(`/channel/referrers/${id}/network`),
  rewards: () => request<unknown[]>('/channel/rewards'),
  rewardSettle: (id: string) => request<{ settled: boolean }>(`/channel/rewards/${id}/settle`, { method: 'POST' }),
  rewardClawback: (id: string) => request<{ clawed: boolean }>(`/channel/rewards/${id}/clawback`, { method: 'POST' }),
};

// ===== contractsApi =====
export const contractsApi = {
  list: () => request<unknown[]>('/contracts'),
  create: (body: Record<string, unknown>) => request<Record<string, unknown>>('/contracts', { method: 'POST', body }),
  detail: (id: string) => request<Record<string, unknown>>(`/contracts/${id}`),
  send: (id: string) => request<{ sent: boolean }>(`/contracts/${id}/send`, { method: 'POST' }),
  reminder: (id: string) => request<{ reminded: boolean }>(`/contracts/${id}/reminder`, { method: 'POST' }),
  void: (id: string) => request<{ voided: boolean }>(`/contracts/${id}/void`, { method: 'POST' }),
  templateUpload: (body: Record<string, unknown>) => request<Record<string, unknown>>('/contracts/templates', { method: 'POST', body }),
};

// ===== auditApi =====
export const auditApi = {
  roles: () => request<unknown[]>('/audit/roles'),
  rolePermissions: (id: string, body: { permissionCodes: string[] }) =>
    request<{ updated: boolean }>(`/audit/roles/${id}/permissions`, { method: 'PUT', body }),
  admins: () => request<unknown[]>('/audit/admins'),
  adminCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/audit/admins', { method: 'POST', body }),
  adminUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/audit/admins/${id}`, { method: 'PUT', body }),
  logs: (params?: { page?: number; page_size?: number; module?: string; adminId?: string }) =>
    request<{ list: unknown[]; total: number; page: number; page_size: number }>('/audit/logs', { params }),
  metrics: () => request<Record<string, unknown>>('/audit/metrics'),
};

// ===== reportsApi =====
export const reportsApi = {
  templates: () => request<unknown[]>('/reports/templates'),
  templateCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/reports/templates', { method: 'POST', body }),
  templateUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/reports/templates/${id}`, { method: 'PUT', body }),
  templateRun: (id: string) => request<{ ran: boolean }>(`/reports/templates/${id}/run`, { method: 'POST' }),
  tasks: () => request<unknown[]>('/reports/tasks'),
  taskUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/reports/tasks/${id}`, { method: 'PUT', body }),
  exports: () => request<unknown[]>('/reports/exports'),
  exportDownload: (id: string) => request<{ downloadUrl: string }>(`/reports/exports/${id}/download`),
};

// ===== settingsApi =====
export const settingsApi = {
  security: () => request<Record<string, unknown>>('/settings/security'),
  securitySave: (body: Record<string, unknown>) => request<{ saved: boolean }>('/settings/security', { method: 'PUT', body }),
  securityLogs: () => request<unknown[]>('/settings/security/logs'),
  map: () => request<Record<string, unknown>>('/settings/map'),
  mapSave: (body: Record<string, unknown>) => request<{ saved: boolean }>('/settings/map', { method: 'PUT', body }),
  params: () => request<unknown[]>('/settings/params'),
  paramsSave: (body: Record<string, unknown>) => request<{ saved: boolean }>('/settings/params', { method: 'PUT', body }),
  dicts: () => request<unknown[]>('/settings/dicts'),
  dictCreate: (body: Record<string, unknown>) => request<Record<string, unknown>>('/settings/dicts', { method: 'POST', body }),
  dictUpdate: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/settings/dicts/${id}`, { method: 'PUT', body }),
  i18n: () => request<unknown[]>('/settings/i18n'),
  i18nImport: (body: { locale: string; json: unknown }) => request<{ imported: boolean; locale: string }>('/settings/i18n/import', { method: 'POST', body }),
};

// ===== announcementsApi =====
export const announcementsApi = {
  list: () => request<unknown[]>('/announcements'),
  create: (body: Record<string, unknown>) => request<Record<string, unknown>>('/announcements', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>) => request<{ updated: boolean }>(`/announcements/${id}`, { method: 'PUT', body }),
  publish: (id: string) => request<{ published: boolean }>(`/announcements/${id}/publish`, { method: 'POST' }),
  repush: (id: string) => request<{ repushed: boolean }>(`/announcements/${id}/repush`, { method: 'POST' }),
  revoke: (id: string) => request<{ revoked: boolean }>(`/announcements/${id}/revoke`, { method: 'POST' }),
  stats: (id: string) => request<Record<string, unknown>>(`/announcements/${id}/stats`),
};
